import { useEffect, useMemo, useState } from "react";
import {
  Edit2,
  Plus,
  Save,
  X,
  Archive,
  ArchiveRestore,
  Mail,
  Phone,
  MessageSquare,
  Globe,
} from "lucide-react";
import { api, StructureContact } from "../lib/api";
import { Spinner } from "./Spinner";
import { InlineAlert } from "./InlineAlert";
import { ContactAvatar } from "./ContactAvatar";
import { AvatarUploader } from "./AvatarUploader";
import { DEFAULT_AVATAR_META } from "../lib/avatar";

interface ContactsManagerProps {
  structureId: string;
}

export function ContactsManager({ structureId }: ContactsManagerProps) {
  const [contacts, setContacts] = useState<StructureContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<StructureContact>>({});
  const [saving, setSaving] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [search, setSearch] = useState("");
  const [usageMap, setUsageMap] = useState<Record<string, Array<{ nodeId: string; label: string; role?: string; color?: string }>>>({});
  const [expandedUsage, setExpandedUsage] = useState<Record<string, boolean>>({});
  const buildSignalLink = (value?: string | null) => {
    if (!value) return null;
    const trimmed = value.trim();
    if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
    return `https://signal.me/#p/${encodeURIComponent(trimmed)}`;
  };
  const buildTelegramLink = (value?: string | null) => {
    if (!value) return null;
    const trimmed = value.trim().replace(/^@/, "");
    if (!trimmed) return null;
    if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
    return `https://t.me/${trimmed}`;
  };

  const loadContacts = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.listContacts(structureId, includeArchived);
      setContacts(data.contacts);
    } catch (err: any) {
      setError(err.message || "Fehler beim Laden der Kontakte");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContacts();
  }, [structureId, includeArchived]);

  useEffect(() => {
    const loadUsage = async () => {
      try {
        const graph = await api.getGraph(structureId);
        const usage: Record<string, Array<{ nodeId: string; label: string; role?: string; color?: string }>> = {};
        graph.nodes.forEach((n) => {
          const roles = (n as any).contact_roles || {};
          const ids = (n as any).contact_ids || [];
          ids.forEach((id: string) => {
            if (!usage[id]) usage[id] = [];
            usage[id].push({ nodeId: n.id, label: n.label, role: roles[id], color: (n as any).color });
          });
        });
        setUsageMap(usage);
      } catch (err) {
        console.warn("Usage map load failed", err);
      }
    };
    loadUsage();
  }, [structureId]);

  const startEdit = (contact: StructureContact) => {
    setEditingId(contact.id);
    setEditForm({
      name: contact.name,
      role: contact.role,
      contact_type: contact.contact_type,
      email: contact.email,
      phone: contact.phone,
      signal: contact.signal,
      matrix: contact.matrix,
      telegram: contact.telegram,
      website: contact.website,
      notes: contact.notes,
      tags: contact.tags,
      sort_order: contact.sort_order,
      is_board_member: contact.is_board_member,
      avatar_url: contact.avatar_url || null,
      avatar_meta: contact.avatar_meta || DEFAULT_AVATAR_META,
    });
  };

  const startCreate = () => {
    setIsCreating(true);
    setEditForm({
      name: "",
      role: "",
      contact_type: "primary",
      email: "",
      phone: "",
      signal: "",
      matrix: "",
      telegram: "",
      website: "",
      notes: "",
      tags: [],
      custom_fields: {},
      sort_order: contacts.length,
      is_board_member: false,
      avatar_url: null,
      avatar_meta: DEFAULT_AVATAR_META,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setIsCreating(false);
    setEditForm({});
  };

  const saveContact = async () => {
    setSaving(true);
    setError(null);
    try {
      if (isCreating) {
        if (!editForm.name) {
          setError("Name ist ein Pflichtfeld");
          setSaving(false);
          return;
        }
        await api.createContact(structureId, {
          name: editForm.name,
          role: editForm.role || null,
          contact_type: editForm.contact_type || "primary",
          email: editForm.email || null,
          phone: editForm.phone || null,
          signal: editForm.signal || null,
          matrix: editForm.matrix || null,
          telegram: editForm.telegram || null,
          website: editForm.website || null,
          notes: editForm.notes || null,
          tags: editForm.tags || [],
          custom_fields: editForm.custom_fields || {},
          sort_order: editForm.sort_order || 0,
          is_board_member: editForm.is_board_member || false,
          avatar_url: editForm.avatar_url || null,
          avatar_meta: editForm.avatar_meta || DEFAULT_AVATAR_META,
        });
        setIsCreating(false);
      } else if (editingId) {
        await api.updateContact(structureId, editingId, editForm);
        setEditingId(null);
      }
      setEditForm({});
      await loadContacts();
    } catch (err: any) {
      setError(err.message || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const archiveContact = async (id: string) => {
    try {
      await api.archiveContact(structureId, id);
      await loadContacts();
    } catch (err: any) {
      setError(err.message || "Fehler beim Archivieren");
    }
  };

  const unarchiveContact = async (id: string) => {
    try {
      await api.unarchiveContact(structureId, id);
      await loadContacts();
    } catch (err: any) {
      setError(err.message || "Fehler beim Wiederherstellen");
    }
  };

  const filteredContacts = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = !term
      ? contacts
      : contacts.filter((c) => {
          const values = [
            c.name,
            c.role,
            c.email,
            c.phone,
            c.signal,
            c.matrix,
            c.telegram,
            c.website,
            c.notes,
            ...(c.tags || []),
          ]
            .filter(Boolean)
            .map((v) => v!.toString().toLowerCase());
          return values.some((v) => v.includes(term));
        });
    return [...list].sort((a, b) => a.name.localeCompare(b.name, "de", { sensitivity: "base" }));
  }, [contacts, search]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner label="Lade Kontakte..." size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-2 flex-1">
          <button className="btn btn-primary btn-sm" onClick={startCreate} disabled={isCreating}>
            <Plus className="h-4 w-4" /> Neuer Kontakt
          </button>
          <label className="flex items-center gap-2 text-xs text-[var(--color-muted)] cursor-pointer">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              className="rounded"
            />
            Archivierte anzeigen
          </label>
        </div>
        <div className="w-full md:w-72">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Kontakte suchen..."
            className="w-full rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-3 py-2 text-sm"
          />
        </div>
      </div>

      {error && (
        <InlineAlert tone="error" message={error} dismissible onClose={() => setError(null)} />
      )}

      {/* Create/Edit Form */}
      {isCreating && (
        <div className="border border-[var(--color-border)] rounded-lg p-4 bg-[var(--color-panel-2)] space-y-3">
          <h5 className="text-sm font-semibold">Neuen Kontakt erstellen</h5>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[var(--color-muted)]">Name *</label>
              <input
                type="text"
                className="w-full mt-1 rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-3 py-2 text-sm"
                value={editForm.name || ""}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="Max Mustermann"
              />
            </div>
            <div>
              <label className="text-xs text-[var(--color-muted)]">Rolle</label>
              <input
                type="text"
                className="w-full mt-1 rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-3 py-2 text-sm"
                value={editForm.role || ""}
                onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                placeholder="Vorsitzende, Kassierer, etc."
              />
            </div>
          </div>

          <AvatarUploader
            name={editForm.name || "Neuer Kontakt"}
            value={(editForm.avatar_url as string | null) || null}
            meta={editForm.avatar_meta || DEFAULT_AVATAR_META}
            onChange={(avatar_url, avatar_meta) => setEditForm({ ...editForm, avatar_url, avatar_meta })}
            disabled={saving}
          />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[var(--color-muted)]">E-Mail</label>
              <input
                type="email"
                className="w-full mt-1 rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-3 py-2 text-sm"
                value={editForm.email || ""}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                placeholder="max@example.com"
              />
            </div>
            <div>
              <label className="text-xs text-[var(--color-muted)]">Telefon</label>
              <input
                type="tel"
                className="w-full mt-1 rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-3 py-2 text-sm"
                value={editForm.phone || ""}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                placeholder="+49 123 456789"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[var(--color-muted)]">Signal</label>
              <input
                type="text"
                className="w-full mt-1 rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-3 py-2 text-sm"
                value={editForm.signal || ""}
                onChange={(e) => setEditForm({ ...editForm, signal: e.target.value })}
                placeholder="+49 123 456789"
              />
            </div>
            <div>
              <label className="text-xs text-[var(--color-muted)]">Telegram</label>
              <input
                type="text"
                className="w-full mt-1 rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-3 py-2 text-sm"
                value={editForm.telegram || ""}
                onChange={(e) => setEditForm({ ...editForm, telegram: e.target.value })}
                placeholder="@username"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[var(--color-muted)]">Matrix</label>
              <input
                type="text"
                className="w-full mt-1 rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-3 py-2 text-sm"
                value={editForm.matrix || ""}
                onChange={(e) => setEditForm({ ...editForm, matrix: e.target.value })}
                placeholder="@user:matrix.org"
              />
            </div>
            <div>
              <label className="text-xs text-[var(--color-muted)]">Website</label>
              <input
                type="url"
                className="w-full mt-1 rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-3 py-2 text-sm"
                value={editForm.website || ""}
                onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                placeholder="https://example.com"
              />
            </div>
          </div>

          <div>
            <label className="text-xs text-[var(--color-muted)]">Notizen</label>
            <textarea
              className="w-full mt-1 rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-3 py-2 text-sm"
              rows={3}
              value={editForm.notes || ""}
              onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
              placeholder="Zusätzliche Informationen..."
            />
          </div>

          <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <input
              type="checkbox"
              id="is_board_member"
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              checked={editForm.is_board_member || false}
              onChange={(e) => setEditForm({ ...editForm, is_board_member: e.target.checked })}
            />
            <label htmlFor="is_board_member" className="text-sm text-[var(--color-text)] cursor-pointer">
              <strong>Vorstandskontakt</strong> - Dieser Kontakt ist Mitglied des Vorstands
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button className="btn btn-secondary btn-sm" onClick={cancelEdit} disabled={saving}>
              <X className="h-4 w-4" /> Abbrechen
            </button>
            <button className="btn btn-primary btn-sm" onClick={saveContact} disabled={saving}>
              {saving ? <Spinner size="sm" /> : <Save className="h-4 w-4" />}
              Erstellen
            </button>
          </div>
        </div>
      )}

      {/* Contacts List */}
      <div className="space-y-2">
        {filteredContacts.map((contact) => {
          const isEditing = editingId === contact.id;
          return (
            <div
              key={contact.id}
              className={`border border-[var(--color-border)] rounded-xl p-4 bg-[var(--color-panel-2)] ${
                contact.is_archived ? "opacity-60" : ""
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 space-y-2">
                  <div className="flex items-start gap-3">
                    <ContactAvatar contact={contact} size={40} enablePreview className="mt-0.5 flex-shrink-0" />
                    {!isEditing ? (
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="font-semibold text-sm truncate">{contact.name}</div>
                          {contact.is_board_member && (
                            <span className="text-[11px] px-2 py-0.5 rounded-full border bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 border-purple-300 dark:border-purple-700 whitespace-nowrap">
                              Vorstand
                            </span>
                          )}
                          {contact.is_archived && (
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400">
                              Archiviert
                            </span>
                          )}
                        </div>
                        {contact.role && (
                          <div className="text-xs text-[var(--color-muted)] truncate">{contact.role}</div>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col gap-1">
                        <input
                          className="text-sm rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-2 py-1"
                          value={editForm.name || ""}
                          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        />
                        <input
                          className="text-xs rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-2 py-1"
                          value={editForm.role || ""}
                          onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                          placeholder="Rolle"
                        />
                      </div>
                    )}
                  </div>

                  {!isEditing ? (
                    <>
                      {(contact.email || contact.phone || contact.signal || contact.telegram || contact.matrix || contact.website) && (
                        <div className="mt-2 grid gap-2 sm:grid-cols-2 text-xs">
                          {contact.email && (
                            <a
                              href={`mailto:${contact.email}`}
                              className="flex items-center gap-2 p-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)] hover:border-[var(--color-border-strong)] transition-colors min-w-0 w-full"
                            >
                              <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                                <Mail className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                              </div>
                              <div className="min-w-0 w-full">
                                <div className="text-[11px] uppercase tracking-wide text-[var(--color-muted)]">E-Mail</div>
                                <div className="truncate text-[var(--color-text)] w-full">{contact.email}</div>
                              </div>
                            </a>
                          )}
                          {contact.phone && (
                            <a
                              href={`tel:${contact.phone}`}
                              className="flex items-center gap-2 p-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)] hover:border-[var(--color-border-strong)] transition-colors min-w-0 w-full"
                            >
                              <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                                <Phone className="h-4 w-4 text-green-600 dark:text-green-400" />
                              </div>
                              <div className="min-w-0 w-full">
                                <div className="text-[11px] uppercase tracking-wide text-[var(--color-muted)]">Telefon</div>
                                <div className="truncate text-[var(--color-text)] font-mono w-full">{contact.phone}</div>
                              </div>
                            </a>
                          )}
                          {contact.signal && (
                            <a
                              href={buildSignalLink(contact.signal) || undefined}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 p-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)] hover:border-[var(--color-border-strong)] transition-colors min-w-0 w-full"
                            >
                              <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                                <MessageSquare className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                              </div>
                              <div className="min-w-0 w-full">
                                <div className="text-[11px] uppercase tracking-wide text-[var(--color-muted)]">Signal</div>
                                <div className="truncate text-[var(--color-text)] font-mono w-full">{contact.signal}</div>
                              </div>
                            </a>
                          )}
                          {contact.telegram && (
                            <a
                              href={buildTelegramLink(contact.telegram) || undefined}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 p-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)] hover:border-[var(--color-border-strong)] transition-colors min-w-0 w-full"
                            >
                              <div className="w-8 h-8 rounded-full bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center">
                                <MessageSquare className="h-4 w-4 text-sky-600 dark:text-sky-400" />
                              </div>
                              <div className="min-w-0 w-full">
                                <div className="text-[11px] uppercase tracking-wide text-[var(--color-muted)]">Telegram</div>
                                <div className="truncate text-[var(--color-text)] font-mono w-full">@{contact.telegram.replace(/^@/, "")}</div>
                              </div>
                            </a>
                          )}
                          {contact.matrix && (
                            <div className="flex items-center gap-2 p-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)] min-w-0 w-full">
                              <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                                <MessageSquare className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                              </div>
                              <div className="min-w-0 w-full">
                                <div className="text-[11px] uppercase tracking-wide text-[var(--color-muted)]">Matrix</div>
                                <div className="truncate text-[var(--color-text)] font-mono w-full">{contact.matrix}</div>
                              </div>
                            </div>
                          )}
                          {contact.website && (
                            <a
                              href={contact.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 p-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)] hover:border-[var(--color-border-strong)] transition-colors min-w-0 w-full"
                            >
                              <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                                <Globe className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                              </div>
                              <div className="min-w-0 w-full">
                                <div className="text-[11px] uppercase tracking-wide text-[var(--color-muted)]">Website</div>
                                <div className="truncate text-[var(--color-text)] w-full">{contact.website}</div>
                              </div>
                            </a>
                          )}
                        </div>
                      )}

                      {contact.notes && (
                        <div className="mt-2 text-xs text-[var(--color-muted)] italic border-l-2 border-[var(--color-border)] pl-2">
                          {contact.notes}
                        </div>
                      )}

                      {usageMap[contact.id] && usageMap[contact.id].length > 0 && (
                        <div className="mt-3 text-xs text-[var(--color-muted)]">
                          <div className="font-semibold text-[11px] uppercase tracking-wide mb-1">Verwendung</div>
                          <div className="flex flex-wrap gap-2">
                            {(expandedUsage[contact.id] ? usageMap[contact.id] : usageMap[contact.id].slice(0, 5)).map((u, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[var(--color-border)] bg-[var(--color-panel)] text-[var(--color-text)]"
                              >
                                <span
                                  className="w-2.5 h-2.5 rounded-full border border-[var(--color-border)]"
                                  style={{ background: u.color || "var(--color-panel-2)" }}
                                  title={u.label}
                                />
                                <span className="truncate">{u.label}</span>
                                {u.role && (
                                  <span className="px-2 py-0.5 rounded-full bg-[var(--color-panel-2)] text-[11px] border border-[var(--color-border)]">
                                    {u.role === "backup" ? "Backup" : "Primär"}
                                  </span>
                                )}
                              </span>
                            ))}
                            {usageMap[contact.id].length > 5 && (
                              <button
                                type="button"
                                onClick={() =>
                                  setExpandedUsage((prev) => ({
                                    ...prev,
                                    [contact.id]: !prev[contact.id],
                                  }))
                                }
                                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[var(--color-border)] bg-[var(--color-panel)] text-[var(--color-text)] hover:border-[var(--color-border-strong)] transition-colors"
                              >
                                {expandedUsage[contact.id]
                                  ? "Weniger"
                                  : `… ${usageMap[contact.id].length - 5} weitere`}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="space-y-3 text-xs text-[var(--color-text)]">
                      <AvatarUploader
                        name={editForm.name || contact.name}
                        value={(editForm.avatar_url as string | null) || contact.avatar_url || null}
                        meta={editForm.avatar_meta || contact.avatar_meta || DEFAULT_AVATAR_META}
                        onChange={(avatar_url, avatar_meta) => setEditForm({ ...editForm, avatar_url, avatar_meta })}
                        disabled={saving}
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="email"
                          className="rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-2 py-1"
                          value={editForm.email || ""}
                          onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                          placeholder="E-Mail"
                        />
                        <input
                          type="tel"
                          className="rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-2 py-1"
                          value={editForm.phone || ""}
                          onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                          placeholder="Telefon"
                        />
                        <input
                          type="text"
                          className="rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-2 py-1"
                          value={editForm.signal || ""}
                          onChange={(e) => setEditForm({ ...editForm, signal: e.target.value })}
                          placeholder="Signal"
                        />
                        <input
                          type="text"
                          className="rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-2 py-1"
                          value={editForm.telegram || ""}
                          onChange={(e) => setEditForm({ ...editForm, telegram: e.target.value })}
                          placeholder="Telegram"
                        />
                        <input
                          type="text"
                          className="rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-2 py-1"
                          value={editForm.matrix || ""}
                          onChange={(e) => setEditForm({ ...editForm, matrix: e.target.value })}
                          placeholder="Matrix"
                        />
                        <input
                          type="url"
                          className="rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-2 py-1"
                          value={editForm.website || ""}
                          onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                          placeholder="Website"
                        />
                      </div>
                      <textarea
                        className="w-full rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-2 py-1"
                        value={editForm.notes || ""}
                        onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                        placeholder="Notizen"
                      />
                      <div className="flex items-center gap-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                        <input
                          type="checkbox"
                          id={`is_board_member_${contact.id}`}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          checked={editForm.is_board_member || false}
                          onChange={(e) => setEditForm({ ...editForm, is_board_member: e.target.checked })}
                        />
                        <label htmlFor={`is_board_member_${contact.id}`} className="text-sm text-[var(--color-text)] cursor-pointer">
                          Vorstandskontakt
                        </label>
                      </div>
                      <div className="flex justify-end gap-2">
                        <button className="btn btn-secondary btn-sm" onClick={cancelEdit} disabled={saving}>
                          <X className="h-4 w-4" /> Abbrechen
                        </button>
                        <button className="btn btn-primary btn-sm" onClick={saveContact} disabled={saving}>
                          {saving ? <Spinner size="sm" /> : <Save className="h-4 w-4" />}
                          Speichern
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-1">
                  {!isEditing && (
                    <button
                      className="p-2 hover:bg-[var(--color-panel-2)] rounded transition-colors"
                      onClick={() => startEdit(contact)}
                      disabled={contact.is_archived}
                      title="Bearbeiten"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                  )}
                  {contact.is_archived ? (
                    <button
                      className="p-2 hover:bg-[var(--color-panel-2)] rounded transition-colors"
                      onClick={() => unarchiveContact(contact.id)}
                      title="Wiederherstellen"
                    >
                      <ArchiveRestore className="h-4 w-4" />
                    </button>
                  ) : (
                    <button
                      className="p-2 hover:bg-[var(--color-panel-2)] rounded transition-colors"
                      onClick={() => archiveContact(contact.id)}
                      title="Archivieren"
                    >
                      <Archive className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredContacts.length === 0 && (
        <div className="text-center py-8 text-[var(--color-muted)]">
          <p className="text-sm">Keine Kontakte vorhanden.</p>
          <button className="btn btn-primary btn-sm mt-4" onClick={startCreate}>
            <Plus className="h-4 w-4" /> Ersten Kontakt erstellen
          </button>
        </div>
      )}
    </div>
  );
}
